/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import { API, Auth, Signer } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';

import awsConfig from '../../../Configuration/environment-config';
import { AWSAuthMode } from '../../../Shared/Constants/App';

import { IGraphQLResponse } from '../../../Shared/Models';
import { TeamAssignmentActionType, ManageScheduleAssignmentsInput, ManageJobAssignmentsInput } from '../../../API';
import {
  AdhocShiftsPayload,
  ManageScheduleCreationInput,
  RetrospectiveShiftsPayload,
} from '../../Vendor/Components/AllJobs/AllJobs.Model';
import { SyncFacilityCoordinates } from '../../Vendor/Components/AllJobs/AllJobs.Model';
import { UpdateScheduleMasterInput } from '../../Vendor/Components/AllSchedules/AllSchedules.Model';

import { AuthenticationService } from '../../../Shared/Services/AuthenticationService';
import { getLoginTokenHeaderWhenReady, getWebInfo, signedFetch } from '../../../Shared/Utilities/utils';

import { manageJobAssignments, manageScheduleAssignments } from '../../../graphql/mutations';
import { VendorAssignmentDto } from '../Models/JobManagementDashboard.Model';

export default class AllJobsApiService {
  // eslint-disable-next-line sonarjs/cognitive-complexity
  static async allJobs(pageNumber: number, limit: number, userId: string, options: Record<string, any> = {}) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'findAllWorkOrders');
      if (!endpointConfig?.endpoint) {
        return { data: [], errors: [{ message: 'Endpoint not found' }] };
      }

      const url = new URL(endpointConfig.endpoint);
      const params = new URLSearchParams();

      params.append('userId', userId);
      params.append('pageNumber', pageNumber.toString());
      params.append('limit', limit.toString());

      if (options.vendorId) {
        params.append('vendorId', options.vendorId);
      }

      const filterFields = ['sortBy', 'sortOrder', 'searchText', 'refreshCache'];

      filterFields?.forEach((field) => {
        if (options[field]) {
          params.append(field, options[field]);
        }
      });
      if (options?.sortBy) {
        params.append('sortBy', options.sortBy);
      }

      const multiValueParams = [
        'jobName',
        'jobNumber',
        'jobAddress',
        'vendorName',
        'vendorNumber',
        'customerName',
        'vendorAdminName',
        'vendorAdminEmail',
        'createdOn',
        'workTicketNumber',
        'stateCode',
        'city',
        'workOrderStatus',
      ];

      multiValueParams?.forEach((key) => {
        const value = options[key];
        if (Array?.isArray(value) && value?.length) {
          value?.forEach((item) => {
            params.append(key, item);
          });
        }
      });

      url.search = params.toString();

      return await signedFetch({
        method: 'GET',
        url: url.toString(),
        requestId: uuidv4(),
      });
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async updateScheduleMaster(
    updateScheduleMasterInput: UpdateScheduleMasterInput[],
    userId?: string,
    username?: string
  ) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'updateScheduleMaster');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }

      const endpointUrl = `${endpointConfig.endpoint}`;

      const response = await signedFetch({
        method: 'PUT',
        url: endpointUrl,
        body: updateScheduleMasterInput,
        requestId: uuidv4(),
        userId,
        username,
      });

      return {
        data: response?.data ?? [],
        errors: response?.errors ?? [],
      };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async manageScheduleAssignments(
    manageScheduleAssignmentsInput: ManageScheduleAssignmentsInput[],
    actionType: TeamAssignmentActionType
  ) {
    try {
      const authHeaders = await AuthenticationService.getAuthHeaders();
      const response: IGraphQLResponse = await API.graphql(
        {
          query: manageScheduleAssignments,
          variables: { manageScheduleAssignmentsInput, actionType },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );

      return {
        data: response.data?.manageScheduleAssignments ?? { successAssignments: [], failedAssignments: [] },
        errors: [],
      };
    } catch (e: any) {
      return {
        data: { successAssignments: [], failedAssignments: [] },
        errors: e.errors,
      };
    }
  }
  // static async getFilterValues(userId: string) {
  //   try {
  //     const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
  //     const findApplicableFiltersResponse: IGraphQLResponse = await API.graphql(
  //       {
  //         query: findApplicableFilters,
  //         variables: {
  //           userId,
  //         },
  //         authMode: AWSAuthMode.COGNITO,
  //       },
  //       { ...getWebInfo(), ...authHeaders }
  //     );
  //     return { data: findApplicableFiltersResponse?.data?.findApplicableFilters ?? [], errors: [] };
  //   } catch (e: any) {
  //     return { data: [], errors: e.errors };
  //   }
  // }

  // static async getFilterValues(userId: string) {
  //   try {
  //     const filterEndpoint = awsConfig.aws_cloud_logic_custom.find(
  //       (apiEndpoint: { name: string }) => apiEndpoint.name === 'getFilterValues'
  //     );
  //     if (!filterEndpoint?.endpoint) {
  //       return { data: [], errors: [{ message: 'Endpoint not found' }] };
  //     }
  //     const requestOptions = {
  //       method: 'GET',
  //       headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-PZY-Client-Type': 'Web' },
  //     };
  //     const queryParams = `?userId=${userId}`;
  //     const endpointWithParams = `${filterEndpoint.endpoint}${queryParams}`;

  //     const response = await fetch(endpointWithParams, requestOptions);
  //     const responseJson = await response.json();
  //     return { data: responseJson ?? [], errors: [] };
  //   } catch (e: any) {
  //     return { data: [], errors: e.errors || [{ message: e.message }] };
  //   }
  // }

  static async getFilterValues(userId: string) {
    try {
      const filterEndpoint = awsConfig.aws_cloud_logic_custom.find(
        (apiEndpoint: { name: string }) => apiEndpoint.name === 'getFilterValues'
      );
      if (!filterEndpoint?.endpoint) {
        return { data: [], errors: [{ message: 'Endpoint not found' }] };
      }

      const url = new URL(filterEndpoint.endpoint);
      url.searchParams.append('userId', userId);

      return await signedFetch({
        method: 'GET',
        url: url.toString(),
      });
    } catch (e: any) {
      return { data: [], errors: e.errors || [{ message: e.message || 'Request failed' }] };
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  // static async allSchedules(
  //   pageNumber: number,
  //   limit: number,
  //   userId: string,
  //   options: {
  //     sortBy?: string;
  //     sortOrder?: string;
  //     searchText?: string;
  //     jobName?: string;
  //     jobNumber?: string;
  //     jobAddress?: string;
  //     stateCode?: string;
  //     city?: string;
  //     vendorName?: string;
  //     customerName?: string;
  //     scheduleName?: string;
  //     vendorAdminName?: string;
  //     scheduleStartDate?: string;
  //     scheduleEndDate?: string;
  //     jobStartDate?: string;
  //     jobEndDate?: string;
  //     scheduleStartTime?: string;
  //     scheduleEndTime?: string;
  //     vendorAdminEmail?: string;
  //     createdOn?: string;
  //     workTicketNumber?: string;
  //     mdScheduleType?: string;
  //     woScheduleMasterId?: string[];
  //   } = {}
  // ) {
  //   try {
  //     const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
  //     const variables: {
  //       pageNumber: number;
  //       limit: number;
  //       userId: string;
  //       sortBy?: string;
  //       sortOrder?: string;
  //       searchText?: string;
  //       jobName?: string;
  //       jobNumber?: string;
  //       jobAddress?: string;
  //       stateCode?: string;
  //       city?: string;
  //       vendorName?: string;
  //       customerName?: string;
  //       scheduleName?: string;
  //       vendorAdminName?: string;
  //       scheduleStartDate?: string;
  //       scheduleEndDate?: string;
  //       jobStartDate?: string;
  //       jobEndDate?: string;
  //       scheduleStartTime?: string;
  //       scheduleEndTime?: string;
  //       vendorAdminEmail?: string;
  //       createdOn?: string;
  //       workTicketNumber?: string;
  //       mdScheduleType?: string;
  //       woScheduleMasterId?: string[];
  //     } = { pageNumber, limit, userId };

  //     if (!isEmpty(pageNumber)) variables.pageNumber = pageNumber;
  //     if (!isEmpty(limit)) variables.limit = limit;
  //     if (!isEmpty(userId)) variables.userId = userId;
  //     if (!isEmpty(options.sortBy)) variables.sortBy = options.sortBy;
  //     if (!isEmpty(options.sortOrder)) variables.sortOrder = options.sortOrder;
  //     if (!isEmpty(options.searchText)) variables.searchText = options.searchText;
  //     if (!isEmpty(options.jobName)) variables.jobName = options.jobName;
  //     if (!isEmpty(options.jobNumber)) variables.jobNumber = options.jobNumber;
  //     if (!isEmpty(options.jobAddress)) variables.jobAddress = options.jobAddress;
  //     if (!isEmpty(options.stateCode)) variables.stateCode = options.stateCode;
  //     if (!isEmpty(options.city)) variables.city = options.city;
  //     if (!isEmpty(options.vendorName)) variables.vendorName = options.vendorName;
  //     if (!isEmpty(options.customerName)) variables.customerName = options.customerName;
  //     if (!isEmpty(options.scheduleName)) variables.scheduleName = options.scheduleName;
  //     if (!isEmpty(options.vendorAdminName)) variables.vendorAdminName = options.vendorAdminName;
  //     if (!isEmpty(options.scheduleStartDate)) variables.scheduleStartDate = options.scheduleStartDate;
  //     if (!isEmpty(options.scheduleEndDate)) variables.scheduleEndDate = options.scheduleEndDate;
  //     if (!isEmpty(options.jobStartDate)) variables.jobStartDate = options.jobStartDate;
  //     if (!isEmpty(options.jobEndDate)) variables.jobEndDate = options.jobEndDate;
  //     if (!isEmpty(options.scheduleStartTime)) variables.scheduleStartTime = options.scheduleStartTime;
  //     if (!isEmpty(options.scheduleEndTime)) variables.scheduleEndTime = options.scheduleEndTime;
  //     if (!isEmpty(options.vendorAdminEmail)) variables.vendorAdminEmail = options.vendorAdminEmail;
  //     if (!isEmpty(options.createdOn)) variables.createdOn = options.createdOn;
  //     if (!isEmpty(options.workTicketNumber)) variables.workTicketNumber = options.workTicketNumber;
  //     if (!isEmpty(options.mdScheduleType)) variables.mdScheduleType = options.mdScheduleType;
  //     if (!isEmpty(options.woScheduleMasterId)) {
  //       variables.woScheduleMasterId = options.woScheduleMasterId;
  //     }

  //     const allSchedulesResponse: IGraphQLResponse = await API.graphql(
  //       {
  //         query: allScheduleMasterDetailsV2,
  //         variables: {
  //           scheduleMasterQueryParams: {
  //             ...variables,
  //           },
  //         },
  //         authMode: AWSAuthMode.COGNITO,
  //       },
  //       { ...getWebInfo(), ...authHeaders }
  //     );
  //     return { data: allSchedulesResponse?.data?.allScheduleMasterDetailsV2 ?? [], errors: [] };
  //   } catch (e: any) {
  //     return { data: [], errors: e.errors };
  //   }
  // }

  static async allSchedules(pageNumber: number, limit: number, userId: string, options: Record<string, any> = {}) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'allSchedules');
      if (!endpointConfig?.endpoint) {
        return { data: [], errors: [{ message: 'Endpoint not found' }] };
      }

      const url = new URL(endpointConfig.endpoint);
      const params = new URLSearchParams();

      params.append('userId', userId);
      params.append('pageNumber', pageNumber.toString());
      params.append('limit', limit.toString());

      if (options.vendorId) {
        params.append('vendorId', options.vendorId);
      }

      const filterFields = ['sortBy', 'sortOrder', 'searchText', 'refreshCache'];

      filterFields?.forEach((field) => {
        if (options[field]) {
          params.append(field, options[field]);
        }
      });

      if (options?.woScheduleMasterId?.length) {
        options?.woScheduleMasterId?.forEach((id: string) => {
          params.append('woScheduleMasterId', id);
        });
      }

      if (options?.workOrderId?.length) {
        options?.workOrderId?.forEach((id: string) => {
          params.append('workOrderId', id);
        });
      }

      const multiValueParams = [
        'jobName',
        'jobNumber',
        'jobAddress',
        'stateCode',
        'city',
        'vendorName',
        'customerName',
        'scheduleName',
        'vendorAdminName',
        'scheduleStartDate',
        'scheduleEndDate',
        'jobStartDate',
        'jobEndDate',
        'scheduleStartTime',
        'scheduleEndTime',
        'vendorAdminEmail',
        'createdOn',
        'workTicketNumber',
        'mdScheduleType',
      ];

      multiValueParams?.forEach((key) => {
        const value = options[key];
        if (Array?.isArray(value) && value?.length) {
          value?.forEach((item) => {
            params.append(key, item);
          });
        }
      });

      if (options.woScheduleMasterId?.length) {
        options.woScheduleMasterId.forEach((id: string) => {
          params.append('woScheduleMasterId', id);
        });
      }

      if (options.workOrderId?.length) {
        options.workOrderId.forEach((id: string) => {
          params.append('workOrderId', id);
        });
      }

      url.search = params.toString();

      return await signedFetch({
        method: 'GET',
        url: url.toString(),
      });
    } catch (error) {
      return {
        data: [],
        errors: [{ message: (error as Error).message }],
      };
    }
  }

  static async manageJobAssignments(
    manageJobAssignmentsInput: ManageJobAssignmentsInput[],
    actionType: TeamAssignmentActionType
  ) {
    try {
      const authHeaders = await AuthenticationService.getAuthHeaders();
      const response: IGraphQLResponse = await API.graphql(
        {
          query: manageJobAssignments,
          variables: { manageJobAssignmentsInput, actionType },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return {
        data: response?.data?.manageJobAssignments ?? null,
        errors: [],
      };
    } catch (e: any) {
      return {
        data: { manageJobAssignmentsSuccess: [], manageJobAssignmentsFailure: [] },
        errors: e.errors,
      };
    }
  }

  static async generateGenericUserName(vendorNumber: string) {
    try {
      const credentials = await Auth.currentCredentials();

      if (!credentials.accessKeyId || !credentials.secretAccessKey) {
        throw new Error('Invalid AWS credentials');
      }

      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'generateGenericUserName');

      if (!endpointConfig?.endpoint) {
        return { data: [], errors: [{ message: 'Endpoint not found' }] };
      }

      const url = new URL(`${endpointConfig.endpoint}/${vendorNumber}/next-available-user-id`);

      const request = {
        method: 'GET',
        url: url.toString(),
        headers: {
          'Content-Type': 'application/json',
          'X-PZY-Client-Type': 'Web',
          ...(await getLoginTokenHeaderWhenReady()),
        },
      };

      const signedRequest = Signer.sign(
        request,
        {
          access_key: credentials.accessKeyId,
          secret_key: credentials.secretAccessKey,
          session_token: credentials.sessionToken,
        },
        {
          service: 'execute-api',
          region: awsConfig.aws_project_region,
        }
      );

      const response = await fetch(signedRequest.url, {
        method: signedRequest.method,
        headers: signedRequest.headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      }

      const responseText = await response.text();
      return {
        data: responseText ? [responseText] : [],
        errors: [],
      };
    } catch (error: unknown) {
      const err = error as { message?: string; errors?: Array<{ message: string }> };
      return {
        data: [],
        errors: err?.errors || [{ message: err?.message || 'Request failed' }],
      };
    }
  }

  static async setUserPassword(userId: string, password: string, isPermanentPassword: boolean) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'setUserPassword');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }
      const endpointUrl = `${endpointConfig.endpoint}/${userId}`;
      const result = await signedFetch({
        method: 'PUT',
        url: endpointUrl,
        body: { password, isPermanentPassword },
      });
      return {
        success: true,
        data: result.data,
        errors: result.errors || [],
      };
    } catch (error: any) {
      console.error('Password update failed:', error);
      return {
        success: false,
        errors: error.errors || [{ message: error.message || 'Request failed' }],
      };
    }
  }

  static async getMasterServices(woScheduleMasterId: string) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'getMasterServices');
      if (!endpointConfig?.endpoint) {
        return { data: [], errors: [{ message: 'Endpoint not found' }] };
      }
      const url = new URL(`${endpointConfig.endpoint}/${woScheduleMasterId}`);
      return await signedFetch({
        method: 'GET',
        url: url.toString(),
      });
    } catch (e: any) {
      return { data: [], errors: e.errors || [{ message: e.message || 'Request failed' }] };
    }
  }

  // static async getMasterServices(woScheduleMasterId: string) {
  //   try {
  //     const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
  //     const findApplicableFiltersResponse: IGraphQLResponse = await API.graphql(
  //       {
  //         query: scheduleMasterServices,
  //         variables: {
  //           woScheduleMasterId,
  //         },
  //         authMode: AWSAuthMode.COGNITO,
  //       },
  //       { ...getWebInfo(), ...authHeaders }
  //     );
  //     return { data: findApplicableFiltersResponse?.data?.findApplicableFilters ?? [], errors: [] };
  //   } catch (e: any) {
  //     return { data: [], errors: e.errors };
  //   }
  // }

  static async updateGeoFenceRadius(facilityIds: string[], geoFenceRadius: number) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'updateGeoFence');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }

      const endpointUrl = `${endpointConfig.endpoint}`;

      const response = await signedFetch({
        method: 'PATCH',
        url: endpointUrl,
        body: { facilityIds, geoFenceRadius },
        requestId: uuidv4(),
      });

      return {
        data: response?.data?.[0]?.data,
        errors: response?.data?.[0]?.errors || [],
      };
    } catch (error: any) {
      console.error('GeoFence update failed:', error);
      return {
        success: false,
        errors: error.errors || [{ message: error.message || 'Request failed' }],
      };
    }
  }

  static async updateFacilityCoordinates(
    facilityId: string,
    jobId: string,
    workOrderId: string,
    customerId?: string,
    latitude?: string,
    longitude?: string,
    userId?: string
  ) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'updateFacilityCoordinates');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }

      const endpointUrl = `${endpointConfig.endpoint}`;

      const response = await signedFetch({
        method: 'PATCH',
        url: endpointUrl,
        body: { facilityId, jobId, workOrderId, customerId, latitude, longitude },
        userId,
      });
      return {
        data: response?.data ?? [],
        errors: response?.errors ?? [],
      };
    } catch (error: any) {
      console.error('FacilityCoordinates update failed:', error);
    }
  }

  static async syncFacilityCoordinates(manageSyncFacilityGeoCoverage: SyncFacilityCoordinates[], userId: string) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'syncFacilityCoordinates');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }

      const endpointUrl = `${endpointConfig.endpoint}`;

      const response = await signedFetch({
        method: 'PATCH',
        url: endpointUrl,
        body: manageSyncFacilityGeoCoverage,
        userId,
      });
      return {
        data: response?.data ?? [],
        errors: response?.errors ?? [],
      };
    } catch (error: any) {
      console.error('SyncFacilityCoordinates failed:', error);
    }
  }

  static async manageScheduleAssignmentsEndpoint(
    manageScheduleAssignmentsInput: ManageScheduleAssignmentsInput[],
    actionType: TeamAssignmentActionType,
    userId?: string
  ) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'manageScheduleAssignments');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }

      const endpointUrl = `${endpointConfig.endpoint}`;

      const response = await signedFetch({
        method: 'PATCH',
        url: endpointUrl,
        body: { manageScheduleAssignmentsInput, actionType },
        requestId: uuidv4(),
        userId,
      });
      return {
        data: response.data ?? { successAssignments: [], failedAssignments: [] },
        errors: response?.errors ?? [],
      };
    } catch (e: any) {
      return {
        data: { successAssignments: [], failedAssignments: [] },
        errors: e.errors,
      };
    }
  }

  static async createJobSchedule(manageScheduleCreationInput: ManageScheduleCreationInput, userId?: string) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'createSchedule');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }

      const endpointUrl = `${endpointConfig.endpoint}`;

      const response = await signedFetch({
        method: 'PATCH',
        url: endpointUrl,
        body: manageScheduleCreationInput,
        userId,
      });
      return {
        data: response?.data?.data ?? [],
        errors: response?.errors ?? [],
      };
    } catch (error: any) {
      console.error('Schedule Creation failed:', error);
    }
  }
  static async createAdhocShifts(adhocShiftsPayload: AdhocShiftsPayload, userId?: string) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'createAdhocShifts');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }

      const endpointUrl = `${endpointConfig.endpoint}`;

      const response = await signedFetch({
        method: 'PATCH',
        url: endpointUrl,
        body: adhocShiftsPayload,
        userId,
        requestId: uuidv4(),
      });

      return {
        data: response?.data?.data ?? [],
        errors: response?.errors ?? [],
      };
    } catch (error: any) {
      console.error('Adhoc Shift Creation failed:', error);
    }
  }

  static async createRetrospectiveShift(retrospectiveShiftsPayload: RetrospectiveShiftsPayload, userId?: string) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'createRetrospective');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }

      const endpointUrl = `${endpointConfig.endpoint}`;

      const response = await signedFetch({
        method: 'POST',
        url: endpointUrl,
        body: retrospectiveShiftsPayload,
        userId,
        requestId: uuidv4(),
      });

      return {
        data: response?.data?.data ?? [],
        errors: response?.errors ?? [],
      };
    } catch (error: any) {
      console.error('Retrospective Shift Creation failed:', error);
    }
  }

  static async fetchWorkTickets(jobNumber: string) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'getWorkTickets');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }

      const url = new URL(endpointConfig.endpoint);
      url.searchParams.append('jobNumber', jobNumber);

      const response = await signedFetch({
        method: 'GET',
        url: url.toString(),
      });
      return {
        data: response?.data?.data ?? [],
        errors: response?.errors ?? [],
        status: response?.data?.status ?? null,
      };
    } catch (e: any) {
      console.error(`failed to fetch workTickets for the job number - ${jobNumber}`, e);
    }
  }

  static async manageVendorAssignments(vendorAssignmentDto: VendorAssignmentDto, userId?: string) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'manageVendorAssignments');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }

      const url = new URL(endpointConfig.endpoint);

      const response = await signedFetch({
        method: 'POST',
        url: url.toString(),
        body: vendorAssignmentDto,
        userId,
        requestId: uuidv4(),
      });
      return {
        data: response?.data?.data ?? [],
        errors: response?.errors ?? [],
        status: response?.data?.status ?? null,
      };
    } catch (e: any) {
      console.error(`vendor assignment got failed`, e);
    }
  }

  static async updateRouting(woScheduleMasterId: string, timeAndAttendanceRequired: boolean, userId?: string) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'updateRouting');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }

      const finalEndpoint = endpointConfig?.endpoint?.replace(':woScheduleMasterId', `${woScheduleMasterId}`);
      const url = new URL(finalEndpoint);

      const response = await signedFetch({
        method: 'PATCH',
        url: url.toString(),
        body: { isRoutingRequired: timeAndAttendanceRequired },
        userId: userId,
        requestId: uuidv4(),
      });

      return {
        data: response?.data,
        errors: response?.errors || [],
      };
    } catch (error: any) {
      console.error('Routing update failed:', error);
      return {
        success: false,
        errors: error.errors || [{ message: error.message || 'Request failed' }],
      };
    }
  }
}
